import { InfoIcon, SettingsIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import FilterDrawer from '../../../components/filter-drawer/FilterDrawer';
import FilterStateIndicator from '../../../components/filter-state-indicator/FilterStateIndicator';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import NoItemsFound from '../../../components/no-items-found/NoItemsFound';
import Pagination from '../../../components/pagination/Pagination';
import ResourceGrid, {
  Column,
} from '../../../components/resource-grid/ResourceGrid';
//import UnifiedSearch from '../../../components/unified-search/UnifiedSearch';
import { usePagination } from '../../../hooks/usePagination';
import { usePayorOptions } from '../../../hooks/usePayorOptions';
import { useResourceGridActions } from '../../../hooks/useResourceGridActions';
import { getPlansList } from '../../../services/plans/listPlans';
import {
  InsurancePlan,
  PlansResponse,
} from '../../../types/plans/types';
import { insuranceStatusOptions } from '../../../utils/insuranceStatusOptions';
import { plansColumns } from '../plans/plansColumns';
import { plansFilterMappings } from '../plans/plansFilterMappings';

interface MarketPlansListProps {
  marketId?: string[];
  practiceIds?: string[];
  providerIds?: string[];
  insuranceIds?: string[];
  state?: string;
  name?: string;
}

interface FilterParams {
  [key: string]: string | string[] | undefined;
  marketId?: string[];
  practiceIds?: string[];
  providerIds?: string[];
  insuranceIds?: string[];
  state?: string;
  statuses?: string[];
  payorNames?: string[];
}
const MarketPlansList: React.FC<MarketPlansListProps> = ({
  marketId,
  practiceIds,
  providerIds,
  insuranceIds,
  state,
  name,
}) => {
  const [columns, setColumns] = useState<Column<InsurancePlan>[]>([]);
  const [enhancedBadges, setEnhancedBadges] = useState(false);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter] = useState<string[]>([]);
  const [filteredPlans] = useState<InsurancePlan[]>(
    []
  );
  const [filteredColumns] = useState<
    Column<InsurancePlan>[]
  >([]);
  const [pageSize] = useState(10);
  const payorOptions = usePayorOptions();

  // Status/Payor filters applied via the FilterDrawer (Market/State are
  // already scoped by the marketId/practiceIds props).
  const [appliedFilters, setAppliedFilters] = useState<
    Record<string, string[]>
  >({});
  const [clearFiltersSignal, setClearFiltersSignal] = useState(false);

  const filters = useMemo<FilterParams>(
    () => ({
      marketId,
      practiceIds,
      providerIds,
      insuranceIds,
      state,
      ...appliedFilters,
    }),
    [
      marketId,
      practiceIds,
      providerIds,
      insuranceIds,
      state,
      appliedFilters,
    ]
  );

  const handleApplyFilters = useCallback(
    (newFilters: Record<string, string[] | string>) => {
      const formattedFilters: Record<string, string[]> = {};

      Object.entries(newFilters).forEach(([uiKey, value]) => {
        if (
          value &&
          (Array.isArray(value) ? value.length > 0 : true)
        ) {
          const apiKey = plansFilterMappings[uiKey] || uiKey;
          formattedFilters[apiKey] = Array.isArray(value)
            ? value
            : [value];
        }
      });

      setAppliedFilters(formattedFilters);
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setAppliedFilters({});
    setClearFiltersSignal((prev) => !prev);
  }, []);

  // Resource grid actions for standardized action menu
  const resourceGridContext = {
    scopeType: 'market' as const,
    scopeId: marketId?.[0] || 'all',
    activeFilters: filters,
    columnsMode: 'plans' as const,
  };
  const resourceGridActions = useResourceGridActions(
    resourceGridContext
  );

  // Create stable fetch function for pagination
  const fetchPlans = useCallback(
    (startToken: string | null) =>
      getPlansList(startToken, filters, pageSize),
    [filters, pageSize]
  );

  // Use pagination hook for efficient data loading
  const {
    items: plans,
    loading,
    currentPage,
    totalPages,
    totalCount,
    handlePageChange,
  } = usePagination<InsurancePlan, PlansResponse>(
    fetchPlans,
    pageSize,
    {
      useClientSidePagination: false,
    },
    filters
  );

  // Note: We don't update the parent count here because Market Detail tabs
  // should always show the true total from market data, not the current page count
  // const officeLocations = Array.from(
  //   new Map(
  //     plans.map((plan) => [
  //       plan.practice.id,
  //       {
  //         id: plan.practice.id,
  //         name: plan.practice.name || 'Unknown Location', // Handle null by providing a fallback
  //       },
  //     ])
  //   ).values()
  // );

  // Unified search handlers
  // const handleFilteredPlansChange = useCallback(
  //   (filteredPlans: InsurancePlan[]) => {
  //     setFilteredPlans(filteredPlans);
  //   },
  //   []
  // );

  // const handleFilteredColumnsChange = useCallback(
  //   (filteredColumns: Column<InsurancePlan>[]) => {
  //     setFilteredColumns(filteredColumns);
  //   },
  //   []
  // );

  // Memoized processed plans with sorting optimization
  const processedPlans = useMemo(() => {
    const dataToProcess =
      filteredPlans.length > 0 ? filteredPlans : plans;

    if (!dataToProcess.length) return [];

    return dataToProcess.map((plan: InsurancePlan) => ({
      ...plan,
      plans: plan.plans.sort((a, b) =>
        (a.insurance.friendlyName || '').localeCompare(
          b.insurance.friendlyName || ''
        )
      ),
    }));
  }, [plans, filteredPlans]);

  // Memoized columns generation with plan filtering
  const memoizedColumns = useMemo(() => {
    if (!processedPlans.length) return [];
    const baseColumns = plansColumns(
      processedPlans,
      enhancedBadges,
      planFilter
    );
    return filteredColumns.length > 0 ? filteredColumns : baseColumns;
  }, [processedPlans, enhancedBadges, planFilter, filteredColumns]);

  useEffect(() => {
    setColumns(memoizedColumns);
  }, [memoizedColumns]);

  return (
    <>
      <Box mt="1.4rem" mb="2.38rem" pb="2.13rem">
        <Box>
          <Flex
            justifyContent={'space-between'}
            alignItems={'center'}
            mb="1.06rem"
          >
            <Text
              fontSize={'1.25rem'}
              fontWeight={'700'}
              lineHeight={'120%'}
              color="text.secondary"
            >
              {name} Plans
            </Text>
            <Flex gap="1rem" alignItems="center">
              {/* Unified Search */}
              {/* {plans.length > 0 && (
                <UnifiedSearch
                  plans={plans}
                  onFilteredPlansChange={handleFilteredPlansChange}
                  onFilteredColumnsChange={
                    handleFilteredColumnsChange
                  }
                  columns={columns}
                  placeholder="Search plans, practices, providers..."
                  maxWidth="26rem"
                />
              )} */}

              {/* Settings Menu */}
              <Menu>
                <MenuButton
                  as={IconButton}
                  aria-label="Settings"
                  icon={<SettingsIcon />}
                  size="md"
                  variant="outline"
                  h="2.5rem"
                  w="2.5rem"
                  borderRadius="0.375rem"
                  borderColor="gray.300"
                  bg="white"
                  color="gray.700"
                  _hover={{
                    bg: 'gray.50',
                    borderColor: 'gray.400',
                    color: 'gray.800',
                  }}
                  _active={{
                    bg: 'gray.100',
                  }}
                />
                <MenuList zIndex={1000}>
                  <MenuItem
                    onClick={() => setEnhancedBadges(!enhancedBadges)}
                    color={enhancedBadges ? 'blue.600' : 'gray.700'}
                    fontWeight={enhancedBadges ? '600' : '400'}
                  >
                    {enhancedBadges
                      ? '✓ Enhanced Badges ON'
                      : 'Enhanced Badges OFF'}
                  </MenuItem>
                  <MenuItem
                    onClick={() => setIsLegendOpen(true)}
                    icon={<InfoIcon />}
                    color="blue.600"
                  >
                    Status Legend & Help
                  </MenuItem>
                </MenuList>
              </Menu>

              {/* Filter Drawer - Status and Payor (Market/Practice are already scoped) */}
              <FilterDrawer
                filterTitle="Insurance Plans"
                filterOptions={{
                  Status: insuranceStatusOptions,
                  Payor: payorOptions,
                }}
                onApplyFilters={handleApplyFilters}
                clearFilters={clearFiltersSignal}
                initialFilters={(() => {
                  const uiFilters: Record<string, string | string[]> =
                    {};
                  Object.entries(appliedFilters).forEach(
                    ([apiKey, value]) => {
                      const uiKey =
                        Object.keys(plansFilterMappings).find(
                          (key) => plansFilterMappings[key] === apiKey
                        ) || apiKey;
                      uiFilters[uiKey] = value;
                    }
                  );
                  return uiFilters;
                })()}
              />

              {/* <IconButton
                aria-label="Status Legend"
                icon={<InfoIcon />}
                size="md"
                variant="ghost"
                onClick={() => setIsLegendOpen(true)}
                color="blue.500"
                h="2.5rem"
                w="2.5rem"
                borderRadius="0.375rem"
                _hover={{
                  bg: 'blue.50',
                  color: 'blue.600',
                  transform: 'translateY(-1px)',
                  boxShadow: 'sm',
                }}
                _active={{
                  bg: 'blue.100',
                  transform: 'translateY(0)',
                }}
                transition="all 0.2s ease-in-out"
              /> */}
            </Flex>
          </Flex>

          {/* Filter State Indicator */}
          <FilterStateIndicator
            appliedFilters={appliedFilters}
            totalCount={totalCount || 0}
            filteredCount={processedPlans.length}
            resourceType="plans"
            onClearFilters={handleClearFilters}
          />

          {loading ? (
            <PageLoader height="45vh" />
          ) : (searchQuery.trim()
              ? filteredPlans.length > 0
              : plans.length > 0) && columns.length > 0 ? (
            <ResourceGrid<InsurancePlan>
              key={`market-plans-grid-${
                enhancedBadges ? 'enhanced' : 'basic'
              }`}
              data={processedPlans}
              columns={memoizedColumns}
              twoPaneMode={true}
              leftPaneWidth="35%"
              rightPaneMinWidth="560px"
              actionMenuContext={resourceGridContext}
              actionMenuActions={resourceGridActions}
              marketId={marketId?.[0]}
            />
          ) : (
            <NoItemsFound
              mainMessage="No Insurance Plans Found"
              detailMessage="We couldn't find anything matching your search criteria."
              removeFilters={
                searchQuery.trim()
                  ? () => setSearchQuery('')
                  : undefined
              }
            />
          )}

          {/* Helper text showing current page range */}
          {!loading &&
            totalCount > 0 &&
            (searchQuery.trim()
              ? filteredPlans.length > 0
              : plans.length > 0) && (
              <Text
                fontSize="0.875rem"
                color="gray.600"
                mb="1rem"
                textAlign="center"
              >
                {filteredPlans.length > 0 ? (
                  <>
                    Showing {filteredPlans.length} of {plans.length}{' '}
                    results{' '}
                    {filteredPlans.length !== plans.length &&
                      `(filtered from ${totalCount} total)`}
                  </>
                ) : (
                  <>
                    Showing {(currentPage - 1) * pageSize + 1}–
                    {Math.min(currentPage * pageSize, totalCount)} of{' '}
                    {totalCount}
                    {totalCount === 1 ? ' plan' : ' plans'}
                  </>
                )}
              </Text>
            )}

          {/* Pagination UI */}
          {totalPages > 1 &&
            processedPlans.length > 0 &&
            !searchQuery.trim() && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
        </Box>
      </Box>

      {/* Status Legend Modal */}
      <Modal
        isOpen={isLegendOpen}
        onClose={() => setIsLegendOpen(false)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Status Badge Legend & Help</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="2rem">
            <Stack spacing="1.5rem">
              <Box>
                <Text
                  fontSize="1.125rem"
                  fontWeight="600"
                  mb="0.5rem"
                >
                  Enhanced Badge Mode: {enhancedBadges ? 'ON' : 'OFF'}
                </Text>
                <Text color="gray.600" fontSize="0.875rem">
                  {enhancedBadges
                    ? 'Badges show two lines: Title (bold) + Subtitle (smaller) for better readability.'
                    : 'Badges show single line with basic status information.'}
                </Text>
              </Box>

              <Box>
                <Text fontSize="1.125rem" fontWeight="600" mb="1rem">
                  Status Color Meanings
                </Text>
                <Stack spacing="1rem">
                  {[
                    {
                      color: 'green.500',
                      title: 'OK to Schedule',
                      subtitle:
                        'Assigned HMO PCP must be in the tax ID',
                      description:
                        'This provider is fully credentialed and can accept new patients for this insurance plan.',
                      example: 'Schedule Allowed',
                    },
                    {
                      color: 'teal.500',
                      title: 'New patients MUST be 10+ days out',
                      subtitle: 'Provider credentialed and active',
                      description:
                        'VA insurance plan where the provider is credentialed. New patients must be scheduled at least 10 days in advance.',
                      example: 'VA — Schedule Allowed',
                    },
                    {
                      color: 'purple.500',
                      title: 'HMO plan requires exact PCP match',
                      subtitle:
                        'Exact PCP match required for HMO plans',
                      description:
                        "HMO plans require the provider to be the exact PCP listed in the patient's plan.",
                      example: 'PCP Match',
                    },
                    {
                      color: 'blue.500',
                      title: 'Schedule Established patients',
                      subtitle: 'Panel is closed; No new patients',
                      description:
                        'Provider can only see existing patients. New patient panel is closed.',
                      example: 'Established patients only',
                    },
                    {
                      color: 'yellow.500',
                      title: 'Do not schedule',
                      subtitle:
                        'Application/credentialing is in process',
                      description:
                        "Provider's application is being processed. Wait for completion before scheduling.",
                      example: 'Pending Credentialing',
                    },
                    {
                      color: 'red.500',
                      title: 'Do not schedule',
                      subtitle: 'Provider does not take this plan',
                      description:
                        'Provider is not contracted with this insurance plan. Do not schedule patients.',
                      example: 'Not Participating',
                    },
                    {
                      color: 'orange.500',
                      title: 'Status Unknown',
                      subtitle:
                        'Contact administrator for clarification',
                      description:
                        'Status information is unclear. Contact administrator to determine scheduling eligibility.',
                      example: 'No Status',
                    },
                  ].map((item, index) => (
                    <Flex
                      key={index}
                      gap="1rem"
                      alignItems="flex-start"
                    >
                      <Box
                        w="2rem"
                        h="2rem"
                        bg={item.color}
                        borderRadius="0.375rem"
                        flexShrink={0}
                      />
                      <Box flex="1">
                        <Text fontWeight="600" color={item.color}>
                          {item.title}
                        </Text>
                        <Text
                          fontSize="0.875rem"
                          color="gray.600"
                          mb="0.25rem"
                        >
                          {item.subtitle}
                        </Text>
                        <Text
                          fontSize="0.875rem"
                          color="gray.700"
                          mb="0.25rem"
                        >
                          {item.description}
                        </Text>
                        <Text
                          fontSize="0.75rem"
                          color="gray.500"
                          fontStyle="italic"
                        >
                          Example: {item.example}
                        </Text>
                      </Box>
                    </Flex>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Text
                  fontSize="1.125rem"
                  fontWeight="600"
                  mb="0.5rem"
                >
                  How to Use
                </Text>
                <Text fontSize="0.875rem" color="gray.600">
                  • Hover over any status badge to see full details
                  <br />
                  • Use the toggle above to switch between enhanced
                  and basic badge modes
                  <br />• Contact administrator if you see "Status
                  Unknown" or unclear statuses
                </Text>
              </Box>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MarketPlansList;
